// Custom modal for ActiveAdmin batch "add_tags" action.
//
// ActiveAdmin v4 triggers batch actions via JS: it sets #batch_action and calls
// collection_selection.submit(). Programmatic submit() does NOT fire "submit"
// listeners, so we intercept the batch-action control flow in capture phase
// (see app/javascript/application.js import order vs @activeadmin/activeadmin).

import $ from "jquery";
import "select2";

const DEBUG = false;
function debugLog(...args) {
  if (!DEBUG) return;
  // eslint-disable-next-line no-console
  console.log("[batch_add_tags_modal]", ...args);
}

function collectionSelectionForm() {
  return document.getElementById("collection_selection");
}

function selectedRows(form) {
  if (!form) return [];
  return Array.from(form.querySelectorAll(".batch-actions-resource-selection:checked"));
}

function batchActionItemFromEventTarget(target) {
  if (!(target instanceof Element)) return null;
  return target.closest("[data-batch-action-item]");
}

function isAddTagsItem(item) {
  return item?.dataset?.action === "add_tags";
}

function ensureBatchActionValue(form, value) {
  const field = document.getElementById("batch_action");
  if (field) field.value = value;
}

function readAdminTagNames() {
  const el = document.getElementById("admin-batch-tag-names");
  if (!el?.textContent?.trim()) return [];
  try {
    const names = JSON.parse(el.textContent);
    return Array.isArray(names) ? names.filter((n) => typeof n === "string" && n.length > 0) : [];
  } catch {
    return [];
  }
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

async function fetchAdminTagNames() {
  const resp = await fetch("/admin/tags/names.json", {
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
    credentials: "same-origin",
  });
  if (!resp.ok) throw new Error(`Failed to load tag names (HTTP ${resp.status})`);
  const data = await resp.json().catch(() => ({}));
  const names = Array.isArray(data) ? data : data?.names;
  return Array.isArray(names) ? names.filter((n) => typeof n === "string" && n.length > 0) : [];
}

function destroyBatchTagSelect2(select) {
  if (typeof $ === "undefined" || !$.fn?.select2) return;
  const $s = $(select);
  if ($s.data("select2")) {
    $s.select2("destroy");
  }
}

function populateTagSelect(select, names) {
  select.innerHTML = "";
  for (const name of names) {
    const opt = document.createElement("option");
    opt.value = name;
    opt.textContent = name;
    select.appendChild(opt);
  }
}

function initBatchTagSelect2(select, modal) {
  if (typeof $ === "undefined" || !$.fn?.select2) return;
  const $sel = $(select);
  $sel.select2({
    placeholder: "Select tags...",
    allowClear: true,
    width: "100%",
    dropdownParent: $(modal)
  });
}

function selectedTagsDelimited(select) {
  if (typeof $ !== "undefined" && $.fn?.select2 && $(select).data("select2")) {
    const v = $(select).val();
    const arr = Array.isArray(v) ? v : v ? [v] : [];
    return arr.join(", ");
  }
  return Array.from(select.selectedOptions)
    .map((o) => o.value)
    .join(", ");
}

function ensureModal() {
  let modal = document.getElementById("aa-batch-add-tags-modal");
  if (modal) return modal;

  modal = document.createElement("div");
  modal.id = "aa-batch-add-tags-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.style.display = "none";
  modal.innerHTML = `
    <div class="aa-batch-add-tags-backdrop" data-aa-batch-add-tags-backdrop></div>
    <div class="aa-batch-add-tags-dialog" role="document" aria-labelledby="aa-batch-add-tags-title">
      <div class="aa-batch-add-tags-header">
        <div class="aa-batch-add-tags-title" id="aa-batch-add-tags-title">Add tags</div>
        <button type="button" class="aa-batch-add-tags-close" data-aa-batch-add-tags-close aria-label="Close">×</button>
      </div>
      <div class="aa-batch-add-tags-body">
        <label class="aa-batch-add-tags-label" for="aa-batch-add-tags-select">Tags</label>
        <div class="aa-batch-add-tags-help">Select from existing tags only.</div>
        <div class="aa-tag-multiselect">
          <select id="aa-batch-add-tags-select" multiple class="tag-multiselect-select" tabindex="0"></select>
        </div>
      </div>
      <div class="aa-batch-add-tags-footer">
        <button type="button" class="aa-batch-add-tags-cancel" data-aa-batch-add-tags-cancel>Cancel</button>
        <button type="button" class="aa-batch-add-tags-submit" data-aa-batch-add-tags-submit>Add tags</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  debugLog("Modal injected into DOM");
  return modal;
}

async function openModal({ title, onSubmit }) {
  debugLog("Opening modal", { title });
  const modal = ensureModal();
  const select = modal.querySelector("#aa-batch-add-tags-select");
  const titleEl = modal.querySelector("#aa-batch-add-tags-title");
  titleEl.textContent = title || "Add tags";

  let tagNames = [];
  try {
    tagNames = await fetchAdminTagNames();
  } catch {
    tagNames = readAdminTagNames();
  }
  destroyBatchTagSelect2(select);
  populateTagSelect(select, tagNames);
  initBatchTagSelect2(select, modal);

  const close = () => {
    destroyBatchTagSelect2(select);
    select.innerHTML = "";
    modal.style.display = "none";
  };

  const submit = () => {
    const value = selectedTagsDelimited(select);
    debugLog("Submitting modal with tags", { value });
    close();
    onSubmit(value);
  };

  modal.querySelector("[data-aa-batch-add-tags-backdrop]")?.addEventListener("click", close, { once: true });
  modal.querySelector("[data-aa-batch-add-tags-close]")?.addEventListener("click", close, { once: true });
  modal.querySelector("[data-aa-batch-add-tags-cancel]")?.addEventListener("click", close, { once: true });
  modal.querySelector("[data-aa-batch-add-tags-submit]")?.addEventListener("click", submit, { once: true });

  modal.style.display = "block";
}

function beginAddTagsModal(form) {
  const checked = selectedRows(form);
  debugLog("Selected rows", { count: checked.length });
  if (checked.length === 0) {
    window.alert("Select at least one record.");
    return;
  }

  void openModal({
    title: "Add tags to selected",
    onSubmit: (tagsValue) => {
      ensureBatchActionValue(form, "add_tags");
      let hidden = form.querySelector('input[name="batch_add_tags[tags]"]');
      if (!hidden) {
        hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "batch_add_tags[tags]";
        form.appendChild(hidden);
      }
      hidden.value = tagsValue;
      debugLog("Submitting collection_selection", { tagsValue });
      form.submit();
    }
  });
}

function install() {
  debugLog("Installing batch add_tags interceptors (capture phase)");

  document.addEventListener(
    "click",
    (event) => {
      const item = batchActionItemFromEventTarget(event.target);
      if (!item || !isAddTagsItem(item)) return;

      // With confirm or AA modal, ActiveAdmin defers submit; handle after confirm.
      if (item.dataset.confirm || item.dataset.modalTarget) return;

      const form = collectionSelectionForm();
      if (!form) {
        debugLog("No collection_selection form");
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      beginAddTagsModal(form);
    },
    true
  );

  document.addEventListener(
    "confirm:complete",
    (event) => {
      if (!(event.target instanceof Element)) return;
      const item = batchActionItemFromEventTarget(event.target);
      if (!item || !isAddTagsItem(item)) return;
      if (event.detail?.[0] !== true) return;

      const form = collectionSelectionForm();
      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      beginAddTagsModal(form);
    },
    true
  );
}

document.addEventListener("DOMContentLoaded", install);

export default {};
